import { useSyncExternalStore } from "react";
import { captureLegacyLoopSnapshot } from "@/lib/loops/product/legacySnapshot";
import type { User } from "./types";

/**
 * UI-presentation store.
 *
 * WP-PT — This store holds NO domain state. Loops, conversations, messages,
 * proposals, Evidence and the account time zone are hosted authority and are
 * read through their hosted adapters. The only things kept here are the
 * mirrored Cloud identity (for rendering) and a handful of harmless,
 * per-browser presentation flags (sidebar collapsed, dismissed cards).
 *
 * The former prototype slices — seeded conversations/messages, search history,
 * notifications, notification settings, editable preferences, sample-data
 * reset, JSON export — are deliberately absent rather than deprecated. Older
 * persisted payloads under the same key are read field-by-field, so stale
 * prototype data can never re-enter the UI.
 */

const KEY = "looseend.state.v4";

export interface AppState {
  hydrated: boolean;
  user: User | null;
  sidebarCollapsed: boolean;
  /** First-use PEE™ onboarding card. */
  peeOnboardingSeen: boolean;
  /** ISO date (yyyy-MM-dd) the PEE™ Summary was last dismissed. */
  peeSummaryDismissedOn: string | null;
  /** Whether the queue intelligence summary is collapsed. */
  peeQueueSummaryDismissed: boolean;
}

const emptyState = (): AppState => ({
  hydrated: false,
  user: null,
  sidebarCollapsed: false,
  peeOnboardingSeen: false,
  peeSummaryDismissedOn: null,
  peeQueueSummaryDismissed: false,
});

/** Only presentation flags are ever persisted. */
const PERSISTED_KEYS = [
  "sidebarCollapsed",
  "peeOnboardingSeen",
  "peeSummaryDismissedOn",
  "peeQueueSummaryDismissed",
] as const;

let state: AppState = emptyState();
const listeners = new Set<() => void>();
const serverSnapshot = emptyState();

function emit() {
  listeners.forEach((l) => l());
}

function persist() {
  if (typeof window === "undefined") return;
  const payload: Record<string, unknown> = {};
  for (const key of PERSISTED_KEYS) payload[key] = state[key];
  try {
    window.localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable — presentation flags simply do not survive reload */
  }
}

function set(patch: Partial<AppState>) {
  state = { ...state, ...patch };
  persist();
  emit();
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return state;
}

export function hydrate() {
  if (state.hydrated || typeof window === "undefined") return;
  // Preserve, then structurally retire, any pre-cutover browser Loops BEFORE the
  // store loads. Legacy Loops can therefore never be read as live authority.
  captureLegacyLoopSnapshot();
  let loaded: Record<string, unknown> = {};
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) loaded = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    loaded = {};
  }
  state = {
    ...emptyState(),
    sidebarCollapsed: loaded["sidebarCollapsed"] === true,
    peeOnboardingSeen: loaded["peeOnboardingSeen"] === true,
    peeSummaryDismissedOn:
      typeof loaded["peeSummaryDismissedOn"] === "string"
        ? (loaded["peeSummaryDismissedOn"] as string)
        : null,
    peeQueueSummaryDismissed: loaded["peeQueueSummaryDismissed"] === true,
    hydrated: true,
  };
  persist();
  emit();
}

export function useAppState(): AppState {
  return useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot);
}

/* ------------------------------------------------------------------ auth */

/**
 * Identity comes from Lovable Cloud authentication. The store only mirrors the
 * authenticated user (immutable Cloud id) so the UI can render it — no tokens
 * or session material are ever kept here or in localStorage.
 */
export const auth = {
  setUser(user: User | null) {
    set({ user });
  },
  clearUser() {
    set({ user: null });
  },
};

/* --------------------------------------------------------------- actions */

export const store = {
  setUser(user: User | null) {
    auth.setUser(user);
  },

  setSidebarCollapsed(collapsed: boolean) {
    set({ sidebarCollapsed: collapsed });
  },
  /** First-use PEE™ onboarding card — dismissed for good. */
  dismissPeeOnboarding() {
    set({ peeOnboardingSeen: true });
  },
  /** Today's PEE™ Summary — dismissed until tomorrow. */
  dismissPeeSummary() {
    set({ peeSummaryDismissedOn: new Date().toISOString().slice(0, 10) });
  },
  dismissQueueSummary(dismissed = true) {
    set({ peeQueueSummaryDismissed: dismissed });
  },
};
